/**
 * SatisfactionTile — Dashboard widget (Jun 2026).
 * Customer 👍/👎 satisfaction: overall score, 6-month trend bars and the
 * latest comment. Data: GET /api/satisfaction/summary (admin + supervisor).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ThumbsUp, ThumbsDown, Loader2, Smile } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const monthLabel = (ym) => {
  try {
    return new Date(`${ym}-15T00:00:00Z`).toLocaleDateString(undefined, { month: 'short' });
  } catch { return ym; }
};

const scoreColor = (s) => (s == null ? '#64748B' : s >= 80 ? '#00C896' : s >= 50 ? '#FFA000' : '#EF4444');

export default function SatisfactionTile() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/satisfaction/summary`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const recent = data?.recent?.find((r) => r.customer_rating_comment);

  return (
    <div className="ht-card p-5 flex flex-col" data-testid="satisfaction-tile">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Smile className="w-4 h-4" style={{ color: '#00C896' }} />
          Customer Satisfaction
        </h3>
        {data?.total > 0 && (
          <span className="text-xs" style={{ color: '#A0AEC0' }}>{data.total} rating{data.total === 1 ? '' : 's'}</span>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
        </div>
      ) : !data || data.total === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-testid="satisfaction-empty">
          <div className="flex gap-2 mb-2 text-slate-600"><ThumbsUp className="w-5 h-5" /><ThumbsDown className="w-5 h-5" /></div>
          <p className="text-sm" style={{ color: '#A0AEC0' }}>No ratings yet</p>
          <p className="text-xs mt-1" style={{ color: '#64748B' }}>
            Customers rate 👍/👎 in their job portal once work is completed.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-4 mb-4">
            <div>
              <div className="text-3xl font-bold" style={{ color: scoreColor(data.score) }} data-testid="satisfaction-score">
                {data.score}%
              </div>
              <div className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>positive</div>
            </div>
            <div className="flex items-center gap-3 pb-1 text-sm">
              <span className="flex items-center gap-1" style={{ color: '#00C896' }}>
                <ThumbsUp className="w-3.5 h-3.5" /> {data.up}
              </span>
              <span className="flex items-center gap-1" style={{ color: '#EF4444' }}>
                <ThumbsDown className="w-3.5 h-3.5" /> {data.down}
              </span>
            </div>
          </div>

          {/* 6-month trend bars */}
          <div className="flex items-end gap-2 h-16 mb-1" data-testid="satisfaction-trend">
            {data.trend.map((t) => {
              const total = t.up + t.down;
              return (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1" title={total ? `${t.month}: ${t.score}% (${t.up}↑ ${t.down}↓)` : `${t.month}: no ratings`}>
                  <div className="w-full flex-1 flex items-end rounded-sm overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    {total > 0 && (
                      <div
                        className="w-full rounded-sm transition-all"
                        style={{ height: `${Math.max(t.score, 8)}%`, background: scoreColor(t.score) }}
                      />
                    )}
                  </div>
                  <span className="text-[10px]" style={{ color: '#64748B' }}>{monthLabel(t.month)}</span>
                </div>
              );
            })}
          </div>

          {recent && (
            <p className="text-xs mt-2 italic truncate" style={{ color: '#A0AEC0' }} title={recent.customer_rating_comment}>
              “{recent.customer_rating_comment}” — {recent.customer_name || 'customer'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
